// --- Dependencies -------------------------------------------------------------------------------
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime, Settings } from "luxon";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useTranslation from "@/hooks/useTranslation";
import "../field.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * **Every date in this product is a Saudi date.** A company in Riyadh books a viewing for Tuesday
 * at four, and that is four o'clock there whether the laptop reading it back is in Riyadh, Cairo or
 * a server in Frankfurt. Pinning the zone once, here, is what stops an installment dated the first
 * of the month showing as the last day of the previous one for somebody on a different machine.
 *
 * It is set as a module side effect on purpose: it has to be true before the first picker renders,
 * and a hook would make it true one render too late.
 */
Settings.defaultZone = constants.timeZone;

type Props = {
    /** An ISO string, or `null` for "nobody said". */
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    /** A day with no clock on it — a signing date, a delivery date, an installment's due date. */
    dateOnly?: boolean;
    min?: Date | null;
    max?: Date | null;
    disabled?: boolean;
};

const toDateTime = (value: Date | null | undefined): DateTime | undefined => (value ? DateTime.fromJSDate(value) : undefined);

/**
 * A date, and usually a time with it — MUI's picker rather than the browser's.
 *
 * The native `datetime-local` was replaced for one reason that does not look like it matters until
 * you are in Riyadh: **it draws itself in the browser's locale, not the page's.** A Saudi company
 * running an English Chrome got `mm/dd/yyyy` on an Arabic screen and nothing in the app could
 * change it. This reads `dd/MM/yyyy` and a twelve-hour clock in both languages, always.
 *
 * **The whole control is `ltr`, in Arabic too, and that is deliberate.** A calendar is a grid with
 * a fixed reading order and a pair of arrows that mean *earlier* and *later* — mirroring it makes
 * the arrows point the wrong way, reverses the weekday header against the dates under it, and puts
 * the hour column after the minutes. None of that is a translation problem; the grid is the same
 * grid in every language, and the only things that should flip are the words, which they do.
 *
 * `min` and `max` are the real work on a contact form: they keep something that happened out of
 * the future and something booked out of the past, inside the control rather than in a refusal
 * after the rest of the form is filled.
 */
export default function DateTimeInput({ value, onChange, label, dateOnly, min, max, disabled }: Props) {
    const t = useTranslation();
    const Picker = dateOnly ? DatePicker : DateTimePicker;

    /**
     * The two pickers take different bound props, and the one that does not recognise a prop passes
     * it straight to the DOM — which React then complains about by name. So the bounds are built
     * per mode rather than handed to both and hoped for.
     */
    const bounds = dateOnly
        ? { ...(min ? { minDate: toDateTime(min) } : {}), ...(max ? { maxDate: toDateTime(max) } : {}) }
        : { ...(min ? { minDateTime: toDateTime(min) } : {}), ...(max ? { maxDateTime: toDateTime(max) } : {}), ampm: true };

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-GB">
            <Picker
                className="field date-input"
                label={label}
                value={value ? DateTime.fromISO(value) : null}
                onChange={(next: DateTime | null) => onChange(next && next.isValid ? next.toUTC().toISO() : null)}
                disabled={disabled}
                {...bounds}
                slotProps={{
                    textField: { fullWidth: true, variant: "outlined", slotProps: { inputLabel: { shrink: true } } },
                    /**
                     * Flipped and clamped. A field near the bottom of a centred form opened its
                     * calendar straight off the end of the viewport — with the buttons that close
                     * it down there too, so it could not be dismissed. `flip` sends it above the
                     * field when there is no room below, and `preventOverflow` on the cross axis
                     * keeps it on screen when neither side quite fits.
                     */
                    popper: {
                        className: "date-input__popper",
                        placement: "bottom-start",
                        modifiers: [
                            { name: "flip", enabled: true, options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"], padding: 12 } },
                            { name: "preventOverflow", enabled: true, options: { altAxis: true, tether: false, padding: 12 } },
                        ],
                    },
                    dialog: { className: "date-input__popper" },
                    actionBar: { actions: ["clear", "accept"] },
                }}
                localeText={{
                    okButtonLabel: t.common.done,
                    cancelButtonLabel: t.common.cancel,
                    clearButtonLabel: t.common.clearFilters,
                    todayButtonLabel: t.crm.today,
                    toolbarTitle: label ?? t.crm.fields.date,
                }}
            />
        </LocalizationProvider>
    );
}
